// deps
import React, { useEffect } from "react";

// internal-deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { ScreenReaderOnly, useEffectOnce } from "@swiggy-private/core-ui";

// components
import { GiftingCartBowTieLine } from "./components/GiftingCartBowTieLine";
import CartSavingsBannerOneLineTextContainer from "./components/CartSavingsBannerOneLineTextContainer";

// utils
import { formatForA11y } from "@super-ui/helpers/String";

// constants
import { SAVINGS_BANNER_TEST_IDS } from "@super-ui/test-ids/savings-banner";

//styles
import { IconWrapper, SavingsBannerOnelineContainer } from "./styles/CartSavingsBannerOneline.style";
import styles from "./styles/CartSavingsBannerOneLine.scss";

export const SAVINGS_ICON_DIMENSION = "12px";
export const SAVINGS_ICON_PATH = "cart_two_dot_o/star.png";

export const TRAILING_ICON_HEIGHT = "18px";

export interface CartSavingsBannerOneLinePropsV2 {
    title: string;
    subtitle: string;
    details: Record<string, number>;
    cssClass?: string;
    pauseUpdate?: boolean;
    onImpression?: VoidFunction;
    onUpdate?: VoidFunction;
    isCartV2?: boolean;
    isGiftingCartFlow?: boolean;
}

const CartSavingsBannerOneLineV2: React.FC<CartSavingsBannerOneLinePropsV2> = ({
    title,
    subtitle,
    details,
    onImpression,
    onUpdate,
    isCartV2,
    isGiftingCartFlow,
}) => {
    useEffect(() => {
        // perform actions required when component data is updated
        onUpdate?.();
    }, [title, subtitle, details, onUpdate]);

    useEffectOnce(() => {
        // perform actions required on component mount
        onImpression?.();
    });

    return (
        <SavingsBannerOnelineContainer
            data-testid={SAVINGS_BANNER_TEST_IDS.CONTAINER}
            isGiftingCartFlow={isGiftingCartFlow}
        >
            {isGiftingCartFlow && <GiftingCartBowTieLine />}
            <IconWrapper isGiftingCartFlow={isGiftingCartFlow}>
                <Image
                    cloudinaryId={SAVINGS_ICON_PATH}
                    alt=""
                    width={SAVINGS_ICON_DIMENSION}
                    height={SAVINGS_ICON_DIMENSION}
                />
            </IconWrapper>
            <CartSavingsBannerOneLineTextContainer
                title={title}
                subtitle={subtitle}
                details={details}
                wrapperClass={styles.wrapperV2}
                // This is needed to reuse the one line sticky styles of Cart V1 in Cart V2
                isBannerSticky={true}
                isCartV2={isCartV2}
                isGiftingCartFlow={isGiftingCartFlow}
            />
            <ScreenReaderOnly text={formatForA11y(title, subtitle, details)} />
        </SavingsBannerOnelineContainer>
    );
};

export default React.memo(CartSavingsBannerOneLineV2, (_, nextProps) => !!nextProps?.pauseUpdate);
